/**
 * connectionValidator — runs on every drag-connect attempt.
 *
 * Allows only connections that produce a coherent flow:
 *   - no self-loops
 *   - target must be able to receive input (trigger has no input)
 *   - source handle must not already have an outgoing edge (each pointer is 1:1)
 *   - no going "backwards": post-purchase nodes (capture_policy / delay /
 *     fraud_check) can't feed back into the pre-checkout graph. The validator
 *     model: every node has a "phase" and a connection is allowed only when
 *     target's phase >= source's phase.
 */

import type { Connection, Edge } from "@xyflow/react";
import type { NodeType } from "../../types/flow";

const INPUTLESS: ReadonlySet<NodeType> = new Set(["trigger"]);

type Phase = "pre" | "checkout" | "post";

const PHASE: Record<NodeType, Phase> = {
  trigger: "pre",
  condition: "pre",
  switch: "pre",
  ab_test: "pre",
  schedule: "pre",
  checkout_page: "checkout",
  capture_policy: "post",
  delay: "post",
  fraud_check: "post",
  // Action nodes (HTTP request, etc.) are phase-neutral — they fire as
  // side-effects from anywhere in the flow. See the explicit bypass below.
  http_request: "pre",
};

// Action nodes don't enforce phase ordering — they can sit on any path.
const PHASE_NEUTRAL: ReadonlySet<NodeType> = new Set(["http_request"]);

const PHASE_RANK: Record<Phase, number> = { pre: 0, checkout: 1, post: 2 };

export interface ConnectionValidationContext {
  sourceType: NodeType;
  targetType: NodeType;
  sourceHandle: string | null | undefined;
  edges: Edge[];
  source: string;
  target: string;
}

export interface ConnectionValidationResult {
  ok: boolean;
  reason?: string;
}

export function validateConnection(
  ctx: ConnectionValidationContext,
): ConnectionValidationResult {
  if (ctx.source === ctx.target) {
    return { ok: false, reason: "A node can't connect to itself." };
  }
  if (INPUTLESS.has(ctx.targetType)) {
    return { ok: false, reason: "Trigger nodes have no input." };
  }
  if (!ctx.sourceHandle) {
    return { ok: false, reason: "Missing source handle." };
  }
  // One outgoing edge per source handle.
  const already = ctx.edges.find(
    (e) => e.source === ctx.source && e.sourceHandle === ctx.sourceHandle,
  );
  if (already) {
    return {
      ok: false,
      reason: "This output is already connected. Remove the existing line first.",
    };
  }
  // Don't let post-purchase feed back into pre-checkout or checkout. Action
  // nodes (HTTP request, etc.) are exempt — they're phase-neutral by design.
  if (
    !PHASE_NEUTRAL.has(ctx.sourceType) &&
    !PHASE_NEUTRAL.has(ctx.targetType)
  ) {
    const sRank = PHASE_RANK[PHASE[ctx.sourceType]];
    const tRank = PHASE_RANK[PHASE[ctx.targetType]];
    if (tRank < sRank) {
      return {
        ok: false,
        reason: "Post-purchase nodes can't feed back into earlier steps.",
      };
    }
  }
  return { ok: true };
}

/**
 * Thin wrapper for react-flow's isValidConnection signature.
 * Returns a boolean; callers log the reason separately if they want UI feedback.
 */
export function isValidConnection(
  conn: Connection,
  nodeTypeById: (id: string) => NodeType | undefined,
  edges: Edge[],
): boolean {
  if (!conn.source || !conn.target) return false;
  const sourceType = nodeTypeById(conn.source);
  const targetType = nodeTypeById(conn.target);
  if (!sourceType || !targetType) return false;
  return validateConnection({
    source: conn.source,
    target: conn.target,
    sourceType,
    targetType,
    sourceHandle: conn.sourceHandle,
    edges,
  }).ok;
}

/** Every node type the user can ADD to the canvas (everything except Trigger,
 * which is the immutable start node). Used by both the right-click "add at
 * cursor" menu and the click-from-output menu. */
export const ADDABLE_NODE_TYPES: readonly NodeType[] = [
  "condition",
  "switch",
  "ab_test",
  "schedule",
  "checkout_page",
  "capture_policy",
  "delay",
  "fraud_check",
  "http_request",
];

/**
 * Given a source handle (an existing node's output), return the subset of
 * addable node types that would form a valid connection if the new node were
 * dropped under it. Driven by validateConnection, so phase rules + duplicate-
 * edge rules are honored.
 */
export function validTargetsForOutput(
  sourceId: string,
  sourceType: NodeType,
  sourceHandle: string,
  edges: Edge[],
): NodeType[] {
  return ADDABLE_NODE_TYPES.filter((targetType) =>
    validateConnection({
      source: sourceId,
      target: "__new__",
      sourceType,
      targetType,
      sourceHandle,
      edges,
    }).ok,
  );
}
